"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import AddAnotherReturnModal from "../../components/modals/AddReturnModel"
import CelebrateModal from "../../components/modals/CelebrateModal"
import { UpdateModal } from "../../components/modals"
import Navbar from "../../components/account/Navbar"
import { petAPI, userAPI } from "../../services/api"
import Footer from "../../components/account/Footer"
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType"
import "./PetStyles.css"
import { useTranslation } from "react-i18next"
import { useAuth } from "../../hooks/useAuth"
import { getUserTypeColor } from "../../utils/userTypeUtils"
import { USER_TYPES } from "../../constants/userTypes"

// Import Navbar and Footer components (assuming they exist)

const EditPet = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userType, petId } = useParams() // Added petId from URL params
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { user } = useAuth();

  // Check if user is a Rescuer (subtype check)
  // Fetch from API since subtype might not be in the user object
  const [isRescuer, setIsRescuer] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkRescuerStatus = async () => {
      try {
        // First check localStorage and user object (quick check)
        let rescuerCheck = false;

        // Check localStorage
        try {
          const storedUserData = localStorage.getItem("user_data");
          if (storedUserData) {
            const parsedUser = JSON.parse(storedUserData);
            const subType = parsedUser.sub_type || parsedUser.subType;
            const subTypeDisplay = parsedUser.sub_type_display_name || parsedUser.subTypeDisplayName;

            rescuerCheck = (
              (subType && subType.toLowerCase() === "rescuer") ||
              (subTypeDisplay && subTypeDisplay.toLowerCase() === "rescuer")
            );
          }
        } catch (error) {
          console.error("Error parsing user_data from localStorage:", error);
        }

        // Check user object from useAuth
        if (!rescuerCheck && user) {
          const subType = user.sub_type || user.subType;
          const subTypeDisplay = user.sub_type_display_name || user.subTypeDisplayName;

          rescuerCheck = (
            (subType && subType.toLowerCase() === "rescuer") ||
            (subTypeDisplay && subTypeDisplay.toLowerCase() === "rescuer")
          );
        }

        // If still not found, fetch from API
        if (!rescuerCheck) {
          const response = await userAPI.getProfile();
          const payload = response.data?.data || response.data;

          if (!cancelled && payload) {
            const subType = payload.sub_type || payload.subType;
            const subTypeDisplay = payload.sub_type_display_name || payload.subTypeDisplayName;

            rescuerCheck = (
              (subType && subType.toLowerCase() === "rescuer") ||
              (subTypeDisplay && subTypeDisplay.toLowerCase() === "rescuer")
            );
          }
        }

        if (!cancelled) {
          setIsRescuer(rescuerCheck);
        }
      } catch (error) {
        console.error("Error checking rescuer status:", error);
        if (!cancelled) {
          setIsRescuer(false);
        }
      }
    };

    checkRescuerStatus();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Apply user type color to Flatpickr calendars globally
  const userTypeColor = useFlatpickrUserType();
  const userTypeColorHover = `${userTypeColor}E6`; // Adding alpha for hover or just using the same color

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showCelebrateModal, setShowCelebrateModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [errors, setErrors] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true) // New state for initial data loading
  const [speciesList, setSpeciesList] = useState([]);
  const [breedList, setBreedList] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const birthDateRef = useRef(null);
  const adoptionDateRef = useRef(null);

  const [formData, setFormData] = useState({
    gender: "",
    adoptedRescued: "adopted", // Default to "adopted"
    petName: "",
    color: "",
    provider: "",
    providerNumber: "",
    policyNumber: "",
    species: "",
    breed: "",
    spayedNeutered: "",
    microchipId: "",
    smartCollarId: "",
    birth_date: "",
    weight: "",
    adoptionDate: "",
    interests: [],
    petImagePreview: null,
    petImageFile: null,
  })

  // Store raw pet data until species list is loaded
  const [rawPetData, setRawPetData] = useState(null)

  // Refs to prevent duplicate API calls
  const petDataFetched = useRef(false)
  const speciesListLoaded = useRef(false)
  const breedMatchingData = useRef(null)



  // Fetch pet data on component mount
  useEffect(() => {
    const fetchPetData = async () => {
      if (!petId || petDataFetched.current) {
        setIsLoadingData(false)
        return
      }

      try {
        setIsLoadingData(true)
        setErrors([])
        petDataFetched.current = true

        const response = await petAPI.getPet(petId)
        if (response.data && response.data.status === 'success' && response.data.data) {
          const pet = response.data.data
          // Store raw pet data to process after species list loads
          setRawPetData(pet)
          const interests = pet.bio ? pet.bio.split(", ").filter(i => i.trim()) : []

          // Convert birth date from YYYY-MM-DD to DD-MM-YYYY for display
          let formattedBirthDate = "";
          if (pet.birth_date) {
            const dateParts = pet.birth_date.split('-');
            if (dateParts.length === 3) {
              formattedBirthDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
            } else {
              formattedBirthDate = pet.birth_date;
            }
          }

          // Convert adoption date from YYYY-MM-DD to DD-MM-YYYY for display
          let formattedAdoptionDate = "";
          if (pet.adoption_date) {
            const dateParts = pet.adoption_date.split('-');
            if (dateParts.length === 3) {
              formattedAdoptionDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
            } else {
              formattedAdoptionDate = pet.adoption_date;
            }
          }

          // Handle profile image - check if it's an object or string
          let profileImageUrl = null;
          if (pet.profile_image_path) {
            if (typeof pet.profile_image_path === 'string') {
              profileImageUrl = pet.profile_image_path;
            } else if (typeof pet.profile_image_path === 'object' && pet.profile_image_path.url) {
              profileImageUrl = pet.profile_image_path.url;
            } else if (typeof pet.profile_image_path === 'object' && Object.keys(pet.profile_image_path).length === 0) {
              // Handle empty object case - no profile image
              profileImageUrl = null;
            }
          }

          // Handle species and breed - they are now objects with id and name
          const speciesValue = pet.species && typeof pet.species === 'object' ? pet.species.id : pet.species || "";
          const breedValue = pet.breed && typeof pet.breed === 'object' ? pet.breed.id : pet.breed || "";

          // Transform the pet data to match our form structure
          setFormData({
            gender: pet.gender || "",
            adoptedRescued: pet.adopted_rescued || "adopted", // Default to "adopted" if not set
            petName: pet.name || "",
            color: pet.color || "",
            provider: pet.provider_name || "",
            providerNumber: pet.provider_number || "",
            policyNumber: pet.policy_number || "",
            species: speciesList.find(s => s.name === pet.species)?.id || pet.species_id || "",
            breed: pet.breed || "",
            spayedNeutered: pet.is_spayed_neutered ? "yes" : "no",
            microchipId: pet.microchip_id || "",
            smartCollarId: pet.smart_collar_id || "",
            birth_date: formattedBirthDate,
            weight: pet.weight ? String(pet.weight).replace(" kg", "") : "",
            adoptionDate: formattedAdoptionDate,
            interests: interests,
            petImagePreview: profileImageUrl,
            petImageFile: null,
          })

        } else {
          setErrors([t("editPet.errors.fetchFailed")])
          console.error('Failed to fetch pet:', response.data?.message || 'Unknown error')
        }
      } catch (err) {
        console.error('Error fetching pet data:', err)
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        })

        let errorMessage = t("editPet.errors.fetchFailed")
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        } else if (err.message) {
          errorMessage = err.message
        }

        setErrors([errorMessage])
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchPetData()
  }, [petId])

  // Reset refs when petId changes
  useEffect(() => {
    petDataFetched.current = false
    breedMatchingData.current = null
    lastFetchedSpecies.current = null
  }, [petId])
  useEffect(() => {
    if (speciesListLoaded.current) return;

    speciesListLoaded.current = true;
    petAPI.getPetsBySpecies()
      .then(res => {
        // If your API returns { data: [...] }
        const species = Array.isArray(res.data) ? res.data : res.data.data || [];
        setSpeciesList(species);
      })
      .catch(err => {
        console.error('Error loading species:', err);
        setSpeciesList([]);
        speciesListLoaded.current = false; // Reset on error
      });
  }, []);

  // Process pet data when both pet data and species list are available
  useEffect(() => {
    if (rawPetData && speciesList.length > 0) {
      const pet = rawPetData;

      // Parse interests from bio field
      const interests = pet.bio ? pet.bio.split(", ").filter(i => i.trim()) : []

      // Convert birth date from YYYY-MM-DD to DD-MM-YYYY for display
      let formattedBirthDate = "";
      if (pet.birth_date) {
        const dateParts = pet.birth_date.split('-');
        if (dateParts.length === 3) {
          formattedBirthDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        } else {
          formattedBirthDate = pet.birth_date;
        }
      }

      // Convert adoption date from YYYY-MM-DD to DD-MM-YYYY for display
      let formattedAdoptionDate = "";
      if (pet.adoption_date) {
        const dateParts = pet.adoption_date.split('-');
        if (dateParts.length === 3) {
          formattedAdoptionDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        } else {
          formattedAdoptionDate = pet.adoption_date;
        }
      }

      // Handle profile image - check if it's an object or string
      let profileImageUrl = null;
      if (pet.profile_image_path) {
        if (typeof pet.profile_image_path === 'string') {
          profileImageUrl = pet.profile_image_path;
        } else if (typeof pet.profile_image_path === 'object' && pet.profile_image_path.url) {
          profileImageUrl = pet.profile_image_path.url;
        }
      }

      // Handle species - it can be an object with id/name or just a string/id
      let speciesId = "";
      let speciesName = "";

      if (pet.species && typeof pet.species === 'object') {
        // Species is an object with id and name
        speciesId = pet.species.id;
        speciesName = pet.species.name;
      } else {
        // Species is a string or ID, need to find it in species list
        speciesName = pet.species;
        speciesId = speciesList.find(s => s.name === pet.species)?.id || pet.species_id || "";
      }

      // Store breed matching data for later use
      breedMatchingData.current = {
        breed: pet.breed,
        breed_id: pet.breed_id
      };

      // Set selectedSpecies to trigger breed loading
      if (speciesId) {
        setSelectedSpecies(speciesId);
      }

      // Transform the pet data to match our form structure
      setFormData({
        gender: pet.gender || "",
        adoptedRescued: pet.adopted_rescued || "adopted", // Default to "adopted" if not set
        petName: pet.name || "",
        color: pet.color || "",
        provider: pet.provider_name || "",
        policyNumber: pet.policy_number || "",
        species: speciesId,
        breed: "", // Will be updated when breed list loads
        spayedNeutered: pet.is_spayed_neutered ? "yes" : "no",
        microchipId: pet.microchip_id || "",
        smartCollarId: pet.smart_collar_id || "",
        birth_date: formattedBirthDate,
        weight: pet.weight ? String(pet.weight).replace(" kg", "") : "",
        adoptionDate: formattedAdoptionDate,
        interests: interests,
        petImagePreview: profileImageUrl,
        petImageFile: null,
      })

      // Set selectedSpecies for breed loading
      setSelectedSpecies(speciesId)
    }
  }, [rawPetData, speciesList]);

  // This effect is now handled in the main pet data processing effect above

  // Use ref to track the last species for which breeds were fetched
  const lastFetchedSpecies = useRef(null);

  useEffect(() => {
    if (selectedSpecies && selectedSpecies !== lastFetchedSpecies.current) {
      lastFetchedSpecies.current = selectedSpecies;

      petAPI.getBreedBySpecies(selectedSpecies)
        .then(res => {
          // If your API returns { data: [...] }
          const breeds = Array.isArray(res.data) ? res.data : res.data.data || [];
          setBreedList(breeds);

          // If we have breed matching data and breed list is now loaded, update breed field with correct ID
          if (breedMatchingData.current && breeds.length > 0) {
            const { breed, breed_id } = breedMatchingData.current;
            let breedId = "";

            // Strategy 1: Handle breed as object (most common case)
            if (breed && typeof breed === 'object' && breed.id) {
              const objectMatch = breeds.find(b => b.id === breed.id);
              if (objectMatch) {
                breedId = breed.id;
              }
            }

            // Strategy 2: Direct ID match (fallback)
            if (!breedId && breed_id) {
              const directMatch = breeds.find(b => b.id === breed_id);
              if (directMatch) {
                breedId = breed_id;
              }
            }

            // Strategy 3: Name matching (if ID match failed)
            if (!breedId && breed) {
              let petBreedName = "";

              if (typeof breed === 'object' && breed.name) {
                petBreedName = breed.name.toLowerCase().trim();
              } else if (typeof breed === 'string') {
                petBreedName = breed.toLowerCase().trim();
              }

              if (petBreedName) {
                const nameMatch = breeds.find(b => {
                  const breedName = b.name?.toLowerCase?.().trim?.();
                  return breedName === petBreedName ||
                    (breedName && petBreedName && breedName.includes(petBreedName)) ||
                    (breedName && petBreedName && petBreedName.includes(breedName));
                });

                if (nameMatch) {
                  breedId = nameMatch.id;
                }
              }
            }

            // Strategy 4: Fallback to raw breed value if it's numeric
            if (!breedId && breed && typeof breed !== 'object' && !isNaN(breed)) {
              const numericMatch = breeds.find(b => b.id === parseInt(breed));
              if (numericMatch) {
                breedId = parseInt(breed);
              }
            }

            if (breedId) {
              setFormData(prev => ({
                ...prev,
                breed: breedId
              }));
            }

            // Clear the matching data after use
            breedMatchingData.current = null;
          }
        })
        .catch(err => {
          console.error('Error fetching breeds:', err);
          setBreedList([]);
        });
    } else {
      setBreedList([]);
      lastFetchedSpecies.current = null;
    }
  }, [selectedSpecies]);

  // Initialize Flatpickr for birth date
  useEffect(() => {
    if (birthDateRef.current && window.flatpickr) {
      const flatpickrInstance = window.flatpickr(birthDateRef.current, {
        dateFormat: "d-m-Y", // DD-MM-YYYY format
        allowInput: true,
        clickOpens: true,
        onChange: function (selectedDates, dateStr) {
          setFormData(prev => ({ ...prev, birth_date: dateStr }));
        }
      });

      return () => {
        if (flatpickrInstance) {
          flatpickrInstance.destroy();
        }
      };
    }
  }, [isLoadingData]); // Initialize after data is loaded

  // Initialize Flatpickr for adoption date
  useEffect(() => {
    if (adoptionDateRef.current && window.flatpickr) {
      const flatpickrInstance = window.flatpickr(adoptionDateRef.current, {
        dateFormat: "d-m-Y", // DD-MM-YYYY format
        allowInput: true,
        clickOpens: true,
        onChange: function (selectedDates, dateStr) {
          setFormData(prev => ({ ...prev, adoptionDate: dateStr }));
        }
      });

      return () => {
        if (flatpickrInstance) {
          flatpickrInstance.destroy();
        }
      };
    }
  }, [isLoadingData]); // Initialize after data is loaded



  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    if (type === "radio") {
      setFormData((prev) => ({ ...prev, [name]: value }))
    } else if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        interests: checked ? [...prev.interests, value] : prev.interests.filter((interest) => interest !== value),
      }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  // Handler for image file input
  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          petImagePreview: reader.result,
          petImageFile: file,
        }))
      }
      reader.readAsDataURL(file)
    } else {
      setFormData((prev) => ({
        ...prev,
        petImagePreview: null,
        petImageFile: null,
      }))
    }
  }

  // Handler to remove current image
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const handleRemoveImage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setFormData((prev) => ({
      ...prev,
      petImagePreview: null,
      petImageFile: null,
    }));
    setIsImageRemoved(true);
  };

  // Handler to auto-capitalize first letter of pet name and other fields
  const handleCapitalizeBlur = (e) => {
    const { name, value } = e.target
    if (value) {
      const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1)
      setFormData((prev) => ({ ...prev, [name]: capitalizedValue }))
    }
  }

  const handleSave = async () => {
    // Clear previous errors
    setErrors([])
    setIsLoading(true)

    try {
      // Validate required fields before sending
      const validationErrors = [];

      if (!formData.petName || formData.petName.trim() === '') {
        validationErrors.push(t("addPet.petNameRequired"));
      }

      if (!formData.birth_date || formData.birth_date.trim() === '') {
        validationErrors.push(t("addPet.birthDateRequired"));
      }

      if (!formData.breed || formData.breed === '') {
        validationErrors.push(t("addPet.breedRequired"));
      }

      // Validate adopted/rescued field if user is a Rescuer
      if (isRescuer && (!formData.adoptedRescued || formData.adoptedRescued.trim() === '')) {
        validationErrors.push(t("addPet.adoptedRescuedRequired") || "Please select whether the pet is adopted or rescued");
      }

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setIsLoading(false);
        return;
      }

      // Convert birth date from DD-MM-YYYY to YYYY-MM-DD for API
      let birthDate = null;
      if (formData.birth_date && formData.birth_date.includes('-')) {
        const dateParts = formData.birth_date.split('-');
        if (dateParts.length === 3) {
          // Convert from DD-MM-YYYY to YYYY-MM-DD
          birthDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        }
      }

      // Convert adoption date from DD-MM-YYYY to YYYY-MM-DD for API
      let adoptionDate = null;
      if (formData.adoptionDate && formData.adoptionDate.includes('-')) {
        const dateParts = formData.adoptionDate.split('-');
        if (dateParts.length === 3) {
          // Convert from DD-MM-YYYY to YYYY-MM-DD
          adoptionDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        }
      }

      // Prepare the API payload based on form data
      const apiPayload = {
        name: formData.petName.trim(),
        species: formData.species || "Unknown",
        breed: formData.breed || "Unknown",
        gender: formData.gender || "unknown",
        adopted_rescued: formData.adoptedRescued || "adopted", // Default to "adopted" if not set
        color: formData.color || "Unknown",
        provider_name: formData.provider || "Unknown",
        provider_number: formData.providerNumber && formData.providerNumber.trim() ? formData.providerNumber.trim() : null,
        policy_number: formData.policyNumber || null,
        birth_date: birthDate,
        adoption_date: adoptionDate,
        microchip_id: formData.microchipId || null,
        smart_collar_id: formData.smartCollarId || null,
        weight: parseFloat(formData.weight) || 0,
        size_category: "medium", // Default value as per your specification
        is_spayed_neutered: formData.spayedNeutered === "yes" ? true : false,
        bio: formData.interests.join(", ") || "No bio provided", // Use interests as bio
        special_notes: null, // Default empty value
        allergies: null, // Default empty value
        last_known_location: "Central Park, New York", // Default value as per your specification
        is_location_tracking_enabled: true, // Default value as per your specification
        is_public_profile: true, // Default value as per your specification
        allow_social_interactions: true // Default value as per your specification
      }

      // Add profile image specific fields
      if (formData.petImageFile) {
        apiPayload.profile_image = formData.petImageFile;
      }

      // Add remove image flag if applicable
      if (isImageRemoved) {
        apiPayload.remove_profile_image = true;
      }

      // Call the API endpoint - use updatePet with all data included
      await petAPI.updatePet(petId, apiPayload)

      // Show success modal
      setShowUpdateModal(true)
      setIsLoading(false)
    } catch (error) {
      console.error("Error updating pet:", error)
      setIsLoading(false)

      // Handle API validation errors
      if (error.response && error.response.data && error.response.data.errors) {
        const apiErrors = error.response.data.errors
        const errorMessages = Object.values(apiErrors).flat()
        console.error('API Validation Errors:', apiErrors)
        console.error('Formatted Error Messages:', errorMessages)
        setErrors(errorMessages)
      } else if (error.response && error.response.data && error.response.data.message) {
        console.error('API Error Message:', error.response.data.message)
        console.error('Full API Response:', error.response.data)
        setErrors([error.response.data.message])
      } else {
        console.error('Unknown Error:', error)
        console.error('Error Object:', {
          message: error.message,
          stack: error.stack,
          response: error.response
        })
        setErrors([t("addPet.genericError")])
      }
    }
  }

  const handleAddAnother = () => {
    setShowSuccessModal(false)
    // Reset form
    setFormData({
      gender: "",
      petName: "",
      color: "",
      provider: "",
      providerNumber: "",
      policyNumber: "",
      species: "",
      breed: "",
      spayedNeutered: "",
      microchipId: "",
      smartCollarId: "",
      age: "",
      weight: "",
      adoptionDate: "",
      interests: [],
      petImagePreview: null,
      petImageFile: null,
    })
  }

  const handleReturnToMain = () => {
    setShowSuccessModal(false)
    navigate(`/pets/${petId}`)
  }

  const handleCreatePost = () => {
    setShowCelebrateModal(false)
    // Navigate to create post page (to be implemented)
    navigate(`/social/create-post`)
  }

  const handleMaybeLater = () => {
    setShowCelebrateModal(false)
    setShowSuccessModal(true)
  }

  const handleUpdateModalClose = () => {
    setShowUpdateModal(false)
    if (location.state?.fromProfile) {
      navigate(`/social/profile`);
    } else {
      navigate(`/pets/${petId}/view`) // Navigate back to pet profile instead of dashboard
    }
  }

  const handleCancel = () => {
    if (location.state?.fromProfile) {
      navigate(`/social/profile`);
    } else {
      navigate(`/dashboard/pets`);
    }
  }

  const petInterests = useMemo(() => [
    { name: t("addPet.interestsList.medicalCare"), value: "Medical Care", imageSrc: "/icons/pets/breeding.svg" },
    { name: t("addPet.interestsList.grooming"), value: "Grooming", imageSrc: "/icons/pets/grooming.svg" },
    { name: t("addPet.interestsList.trainingActivities"), value: "Training & Activities", imageSrc: "/icons/pets/competing.svg" },
    { name: t("addPet.interestsList.boardingDaycare"), value: "Boarding / Day care", imageSrc: "/icons/pets/gathering.svg" },
    { name: t("addPet.interestsList.socializing"), value: "Socializing", imageSrc: "/icons/pets/socializing.svg" },
    { name: t("addPet.interestsList.onlineConsultation"), value: "Online Consultation / Records", imageSrc: "/icons/pets/tech-needs.svg" },
  ], [t])

  // Show error if no petId is provided
  if (!petId) {
    return (
      <div className="addpet-root" dir={i18n.dir()} lang={i18n.language}>
        <Navbar userType={userType} />
        <main className={`addpet-main addpet-center-content ${isRTL ? 'rtl' : 'ltr'}`}>
          <div className="addpet-center-text">
            <div className="addpet-error-title addpet-large-text">
              {t("editPet.noPetId.title")}
            </div>
            <div className="addpet-subtitle">
              {t("editPet.noPetId.subtitle")}
            </div>
            <button
              onClick={() => navigate('/pets')}
              className="addpet-btn-primary"
            >
              {t("editPet.noPetId.back")}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  // Show loading state while fetching data
  if (isLoadingData) {
    return (
      <div className="addpet-root" dir={i18n.dir()} lang={i18n.language}>
        <Navbar userType={userType} />
        <main className={`addpet-main addpet-center-content ${isRTL ? 'rtl' : 'ltr'}`}>
          <div className="addpet-center-text">
            <div className="addpet-loading-text">
              {t("editPet.loading.title")}
            </div>
            <div className="addpet-subtitle">
              {t("editPet.loading.subtitle")}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="addpet-root" dir={i18n.dir()} lang={i18n.language}>
      <Navbar userType={userType} />

      <main className={`addpet-main ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="addpet-container">
          {/* Header Section with Back Button */}
          <div className="addpet-header">
            <button
              onClick={() => {
                if (location.state?.fromProfile) {
                  navigate(`/social/profile`);
                } else {
                  navigate(`/pets`);
                }
              }}
              className="addpet-back-btn"
              style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {t("addPet.back")}
            </button>
          </div>

          {/* Enhanced Profile Picture Upload Section */}
          <div className="addpet-image-section">
            <div className="addpet-image-container">
              <div className="addpet-image-wrapper">
                <div
                  className="addpet-image-upload"
                  onClick={() => document.getElementById("pet-image-upload").click()}
                >
                  {formData.petImagePreview ? (
                    <img
                      src={formData.petImagePreview || "/placeholder.svg"}
                      alt={t("addPet.photo.previewAlt")}
                      className="addpet-preview-image"
                    />
                  ) : (
                    <div className="addpet-upload-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7,10 12,15 17,10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span className="addpet-upload-text">{t("addPet.photo.uploadLabel")}</span>
                    </div>
                  )}
                  <div className="addpet-upload-overlay">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      <path d="M12 20h9" />
                    </svg>
                    <span>{t("addPet.photo.changeLabel")}</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="addpet-hidden-input"
                    id="pet-image-upload"
                  />
                </div>
                {formData.petImagePreview && (
                  <div
                    className="addpet-remove-image-btn"
                    onClick={handleRemoveImage}
                    title={t("addPet.photo.removeLabel") || "Remove photo"}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </div>
                )}
              </div>
              <div className="addpet-image-info">
                <h3 className="addpet-image-title">{t("addPet.photo.title")}</h3>
                <p className="addpet-image-description">
                  {t("addPet.photo.description")}
                </p>
              </div>
            </div>
          </div>

          {/* Basic Information Section */}
          <section className="addpet-form-section">
            <div className="addpet-section-header">
              <h2 className="addpet-section-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {t("addPet.basicInformation")}
              </h2>
            </div>
            <div className="addpet-form-content">


              <div className="addpet-grid-2">
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.petName")}
                  </label>
                  <input
                    type="text"
                    name="petName"
                    value={formData.petName}
                    onChange={handleInputChange}
                    onBlur={handleCapitalizeBlur}
                    placeholder={t("addPet.petNamePlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.color")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    onBlur={handleCapitalizeBlur}
                    placeholder={t("editPet.colorPlaceholder")}
                    className="addpet-input"
                  />
                </div>

                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.species")}
                  </label>
                  <div className="addpet-select-wrapper">
                    <select
                      name="species"
                      value={formData.species}
                      onChange={(e) => {
                        setSelectedSpecies(e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          species: e.target.value,
                        }));
                      }}
                      className="addpet-input addpet-select"
                    >
                      <option value="" disabled>
                        {t("addPet.speciesPlaceholder")}
                      </option>
                      {speciesList.map((species) => (
                        <option key={species.id} value={species.id}>
                          {species.name}
                        </option>
                      ))}
                    </select>
                    <div className="addpet-select-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.breed")} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <div className="addpet-select-wrapper">
                    <select
                      name="breed"
                      value={formData.breed}
                      onChange={handleInputChange}
                      className="addpet-input addpet-select"
                    >
                      <option value="" disabled>
                        {breedList.length === 0 ? t("editPet.selectSpeciesFirst") : t("addPet.breedPlaceholder")}
                      </option>
                      {(breedList || []).map((breed) => (
                        <option key={breed.id} value={breed.id}>
                          {breed.name}
                        </option>
                      ))}
                    </select>
                    <div className="addpet-select-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="addpet-grid-2">
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.gender")}
                  </label>
                  <div className="addpet-radio-group">
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={formData.gender === "female"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.female")}
                      </span>
                    </label>
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={formData.gender === "male"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.male")}
                      </span>
                    </label>
                  </div>
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.adoptedRescued")}
                    {isRescuer ? (
                      <span style={{ color: '#EF4444' }}>*</span>
                    ) : (
                      <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                    )}
                  </label>
                  <div className="addpet-radio-group">
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="adoptedRescued"
                        value="adopted"
                        checked={formData.adoptedRescued === "adopted"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.adopted")}
                      </span>
                    </label>
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="adoptedRescued"
                        value="rescued"
                        checked={formData.adoptedRescued === "rescued"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.rescued")}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Physical Information Section */}
          <section className="addpet-form-section">
            <div className="addpet-section-header">
              <h2 className="addpet-section-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                {t("addPet.physicalInformation")}
              </h2>
            </div>
            <div className="addpet-form-content">
              <div className="addpet-mb-30">
                <label className="addpet-label">
                  {t("addPet.spayedNeutered")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                </label>
                <div className="addpet-radio-group">
                  <label className="addpet-radio-item">
                    <input
                      type="radio"
                      name="spayedNeutered"
                      value="yes"
                      checked={formData.spayedNeutered === "yes"}
                      onChange={handleInputChange}
                      className="addpet-radio"
                    />
                    <span className="addpet-radio-text">
                      {t("addPet.yes")}
                    </span>
                  </label>
                  <label className="addpet-radio-item">
                    <input
                      type="radio"
                      name="spayedNeutered"
                      value="no"
                      checked={formData.spayedNeutered === "no"}
                      onChange={handleInputChange}
                      className="addpet-radio"
                    />
                    <span className="addpet-radio-text">
                      {t("addPet.no")}
                    </span>
                  </label>
                </div>
              </div>

              <div className="addpet-grid-2">
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.birthDate")} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    ref={birthDateRef}
                    type="text"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleInputChange}
                    placeholder={t("editPet.birthDatePlaceholder")}
                    readOnly
                    className="addpet-input"
                    style={{ cursor: "pointer" }}
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.weight")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    placeholder={t("addPet.weightPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.adoptionDate")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    ref={adoptionDateRef}
                    type="text"
                    name="adoptionDate"
                    value={formData.adoptionDate}
                    onChange={handleInputChange}
                    placeholder={t("editPet.adoptionDatePlaceholder")}
                    readOnly
                    className="addpet-input"
                    style={{ cursor: "pointer" }}
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.microchipId")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="microchipId"
                    value={formData.microchipId}
                    onChange={handleInputChange}
                    placeholder={t("addPet.microchipPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.smartCollarId")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="smartCollarId"
                    value={formData.smartCollarId}
                    onChange={handleInputChange}
                    placeholder={t("addPet.smartCollarPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.provider")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="provider"
                    value={formData.provider}
                    onChange={handleInputChange}
                    onBlur={handleCapitalizeBlur}
                    placeholder={t("addPet.providerPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("editPet.providerNumber")}
                  </label>
                  <input
                    type="text"
                    name="providerNumber"
                    value={formData.providerNumber}
                    onChange={handleInputChange}
                    placeholder={t("editPet.providerNumberPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.policyNumber")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="policyNumber"
                    value={formData.policyNumber}
                    onChange={handleInputChange}
                    onBlur={handleCapitalizeBlur}
                    placeholder={t("addPet.policyNumberPlaceholder")}
                    className="addpet-input"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Pet Interests Section */}
          <section className="addpet-form-section">
            <div className="addpet-section-header">
              <h2 className="addpet-section-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {t("addPet.interests")}
              </h2>
            </div>
            <div className="addpet-form-content">
              <div className="addpet-interests">
                {petInterests.map((interest) => (
                  <div
                    key={interest.value || interest.name}
                    className={`addpet-interest-card ${formData.interests.includes(interest.value || interest.name) ? 'selected' : ''}`}
                    onClick={() => {
                      const interestValue = interest.value || interest.name;
                      if (formData.interests.includes(interestValue)) {
                        setFormData((prev) => ({
                          ...prev,
                          interests: prev.interests.filter(
                            (item) => item !== interestValue
                          ),
                        }))
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          interests: [...prev.interests, interestValue],
                        }))
                      }
                    }}
                  >
                    <div className="addpet-interest-icon">
                      <img
                        src={interest.imageSrc || "/placeholder.svg"}
                        alt={interest.name}
                        width={32}
                        height={32}
                      />
                    </div>
                    <div className="addpet-interest-content">
                      <span className="addpet-interest-text">
                        {interest.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Error Display */}
          {errors.length > 0 && (
            <div className="addpet-error-box">
              <div className="addpet-error-title">
                {t("addPet.errorTitle")}
              </div>
              <ul className="addpet-error-list">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="addpet-actions">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="addpet-btn-primary"
            >
              {isLoading ? t("addPet.saving") : t("editPet.save")}
            </button>
            <button
              onClick={handleCancel}
              className="addpet-btn-secondary"
            >
              {t("addPet.cancel")}
            </button>
          </div>
        </div>
      </main>

      <Footer />

      {/* Modals */}
      <AddAnotherReturnModal
        isOpen={showSuccessModal}
        onAddAnother={handleAddAnother}
        onReturnToMain={handleReturnToMain}
      />

      <CelebrateModal
        isOpen={showCelebrateModal}
        onPost={handleCreatePost}
        onMaybeLater={handleMaybeLater}
      />

      <UpdateModal
        isOpen={showUpdateModal}
        onClose={handleUpdateModalClose}
        title={t("editPet.updateModal.title")}
        message={t("editPet.updateModal.message", { name: formData.petName || t("editPet.petFallback") })}
        icon="edit"
        variant="success"
        primaryButtonText={t("editPet.updateModal.primaryButton")}
        onPrimaryAction={handleUpdateModalClose}
        primaryButtonBg={userTypeColor}
        primaryButtonHover={userTypeColorHover}
      />

    </div>
  );
}

export default EditPet


